import type { FC, PointerEvent, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'
import type { Profile } from '@/types'

import Link from 'next/link'
import { useId, useRef } from 'react'
import { ArrowDown, ArrowUpRight } from 'lucide-react'

import {
    motion,
    useMotionTemplate,
    useScroll,
    useSpring,
    useTransform
} from 'motion/react'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Star } from '@/components/generals/star'
import { Magnetic } from '@/components/motion/magnetic'
import { VideoDialog } from '@/components/landing/video-dialog'
import { useMediaQuery } from '@/hooks/use-media-query'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import { EASE_OUT } from '@/constants/motion'

interface HeroSectionProps {
    profile: Profile
}

interface StickerProps {
    label: string
    position: string
    tone: string
    tilt: string
    depth: number
    delay: number
    progress: MotionValue<number>
    animated: boolean
}

interface RotatingBadgeProps {
    rotate: MotionValue<number>
}

const STICKERS = [
    { label: 'Marketing', position: 'left-[-4%] top-[16%] sm:left-[-12%]', tone: 'bg-primary text-primary-foreground', tilt: '-rotate-6', depth: -150, delay: 0.7 },
    { label: 'Events', position: 'right-[-2%] top-[4%] sm:right-[-8%]', tone: 'bg-accent text-accent-foreground', tilt: 'rotate-6', depth: -260, delay: 0.8 },
    { label: 'Communication', position: 'left-[-2%] bottom-[26%] sm:left-[-16%]', tone: 'bg-white text-foreground ring-1 ring-foreground/10', tilt: 'rotate-3', depth: -90, delay: 0.9 },
    { label: 'Creative', position: 'right-[-2%] bottom-[14%] sm:right-[-10%]', tone: 'bg-foreground text-background', tilt: '-rotate-3', depth: -200, delay: 1 }
]

const Sticker: FC<StickerProps> = ({
    label,
    position,
    tone,
    tilt,
    depth,
    delay,
    progress,
    animated
}): ReactNode => {
    const y = useTransform(progress, [0, 1], animated ? [0, depth] : [0, 0])

    return (
        <motion.span aria-hidden='true' style={{ y }} className={cn('absolute z-20', position)}>
            <motion.span
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay, type: 'spring', stiffness: 260, damping: 16 }}
                className={cn('block whitespace-nowrap rounded-full px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] shadow-lg', tone, tilt)}
            >
                {label}
            </motion.span>
        </motion.span>
    )
}

const RotatingBadge: FC<RotatingBadgeProps> = ({ rotate }): ReactNode => {
    const pathId = `badge-path-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

    return (
        <motion.div
            aria-hidden='true'
            style={{ rotate }}
            className='absolute -bottom-8 -left-3 z-30 size-28 sm:-left-10 md:size-32'
        >
            <div className='relative size-full rounded-full bg-accent shadow-xl'>
                <svg viewBox='0 0 120 120' className='animate-spin-slow absolute inset-0 size-full'>
                    <defs>
                        <path id={pathId} d='M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0' />
                    </defs>

                    <text className='fill-foreground font-mono text-[10px] font-bold uppercase tracking-[0.16em]'>
                        <textPath href={`#${pathId}`} textLength='272' lengthAdjust='spacing'>
                            Scroll down • Discover my work •
                        </textPath>
                    </text>
                </svg>

                <span className='absolute inset-0 m-auto flex size-12 items-center justify-center rounded-full bg-foreground text-background'>
                    <ArrowDown className='size-5' />
                </span>
            </div>
        </motion.div>
    )
}

// Profile hero from the brand reference: big orange headline, name and key
// points, portrait on a teal block. Every layer moves at its own speed with the
// scroll, the headline narrows, and the portrait tilts towards the cursor.
export const HeroSection: FC<HeroSectionProps> = ({ profile }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const reduced = usePrefersReducedMotion()
    const finePointer = useMediaQuery('(pointer: fine)')
    const animated = !reduced

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] })
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4, restDelta: 0.0005 })

    const headlineY = useTransform(progress, [0, 1], animated ? [0, -160] : [0, 0])
    const headlineWidth = useTransform(progress, [0, 0.7], animated ? [100, 75] : [100, 100])
    const headlineAxes = useMotionTemplate`'wdth' ${headlineWidth}, 'opsz' 96`
    const introY = useTransform(progress, [0, 1], animated ? [0, -70] : [0, 0])
    const introOpacity = useTransform(progress, [0.2, 0.85], animated ? [1, 0] : [1, 1])
    const portraitY = useTransform(progress, [0, 1], animated ? [0, 140] : [0, 0])
    const portraitScale = useTransform(progress, [0, 1], animated ? [1, 0.88] : [1, 1])
    const blockRotate = useTransform(progress, [0, 1], animated ? [-4, 12] : [-4, -4])
    const blockScale = useTransform(progress, [0, 1], animated ? [1, 1.12] : [1, 1])
    const panelX = useTransform(progress, [0, 1], animated ? [0, -70] : [0, 0])
    const badgeRotate = useTransform(progress, [0, 1], animated ? [0, 320] : [0, 0])

    const tiltX = useSpring(0, { stiffness: 140, damping: 18 })
    const tiltY = useSpring(0, { stiffness: 140, damping: 18 })

    const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
        if (!animated || !finePointer) return

        const rect = event.currentTarget.getBoundingClientRect()

        tiltY.set(((event.clientX - rect.left) / rect.width - 0.5) * 16)
        tiltX.set(((event.clientY - rect.top) / rect.height - 0.5) * -12)
    }

    const resetTilt = () => {
        tiltX.set(0)
        tiltY.set(0)
    }

    const points = [
        { field: 'hero_point_1', value: profile.hero_point_1 },
        { field: 'hero_point_2', value: profile.hero_point_2 },
        { field: 'hero_point_3', value: profile.hero_point_3 }
    ].filter((point): point is { field: string, value: string } => Boolean(point.value?.trim()))

    const fullName = `${profile.first_name} ${profile.last_name}`
    const initials = `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`
    const videoUrl = profile.video_url?.trim()
    const videoLabel = profile.video_label?.trim() ? profile.video_label : null

    return (
        <section
            id='profile'
            ref={sectionRef}
            onPointerMove={handlePointerMove}
            onPointerLeave={resetTilt}
            className='relative isolate overflow-hidden pt-28 pb-24 md:pt-36 md:pb-32'
        >
            <div aria-hidden='true' className='pointer-events-none absolute -right-40 top-24 -z-10 size-[34rem] rounded-full bg-accent/30 blur-3xl' />
            <div aria-hidden='true' className='pointer-events-none absolute -left-40 bottom-0 -z-10 size-[28rem] rounded-full bg-primary/15 blur-3xl' />

            <Container className='grid items-center gap-16 md:grid-cols-12 md:gap-8'>
                <motion.div style={{ y: introY }} className='relative z-10 md:col-span-7'>
                    {profile.status && (
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: EASE_OUT }}
                            className='inline-flex items-center gap-2.5 rounded-full border border-foreground/10 bg-white px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] shadow-sm'
                        >
                            <span aria-hidden='true' className='relative flex size-2'>
                                <span className='absolute inline-flex size-full animate-ping rounded-full bg-secondary opacity-70' />
                                <span className='relative inline-flex size-2 rounded-full bg-secondary' />
                            </span>

                            <span data-mdlfy-cms={`profile:${profile.id}:status`}>{profile.status}</span>
                        </motion.p>
                    )}

                    {profile.headline && (
                        <motion.div style={{ y: headlineY, fontVariationSettings: headlineAxes }} className='mt-6'>
                            <div className='overflow-hidden pb-[0.2em]'>
                                <motion.p
                                    initial={{ y: '110%' }}
                                    animate={{ y: '0%' }}
                                    transition={{ duration: 1.1, ease: EASE_OUT, delay: 0.1 }}
                                    className='font-heading text-[clamp(3.4rem,10.5vw,9.5rem)] font-extrabold leading-[0.92] tracking-[-0.035em] text-tangerine'
                                >
                                    <span data-mdlfy-cms={`profile:${profile.id}:headline`}>{profile.headline}</span>
                                </motion.p>
                            </div>
                        </motion.div>
                    )}

                    <motion.div style={{ opacity: introOpacity }}>
                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.35 }}
                            className='mt-6 font-heading text-3xl font-bold tracking-tight md:mt-8 md:text-4xl'
                        >
                            <span data-mdlfy-cms={`profile:${profile.id}:first_name`}>{profile.first_name}</span>
                            {' '}
                            <span className='uppercase' data-mdlfy-cms={`profile:${profile.id}:last_name`}>{profile.last_name}</span>
                        </motion.h1>

                        {points.length > 0 && (
                            <ul className='mt-5 space-y-3'>
                                {points.map((point, index) => (
                                    <motion.li
                                        key={point.field}
                                        initial={{ opacity: 0, x: -24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.45 + index * 0.1 }}
                                        className='flex items-start gap-3 text-base leading-snug md:text-lg'
                                    >
                                        <Star className='mt-1 size-4 shrink-0 text-secondary' />

                                        <span data-mdlfy-cms={`profile:${profile.id}:${point.field}`}>{point.value}</span>
                                    </motion.li>
                                ))}
                            </ul>
                        )}

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.8 }}
                            className='mt-10 flex flex-wrap items-center gap-3'
                        >
                            {videoUrl ? (
                                <VideoDialog
                                    url={videoUrl}
                                    label={videoLabel ?? 'My video presentation'}
                                    cms={videoLabel ? `profile:${profile.id}:video_label` : undefined}
                                />
                            ) : (
                                <Link
                                    href='#contact'
                                    className='inline-flex items-center rounded-full bg-accent px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.14em] text-accent-foreground shadow-[0_10px_30px_-12px_rgba(255,166,236,0.9)] transition-transform duration-300 hover:-translate-y-0.5'
                                >
                                    Get in touch
                                </Link>
                            )}

                            <Magnetic>
                                <Link
                                    href='#work'
                                    className='group inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.14em] text-background transition-colors duration-300 hover:bg-secondary'
                                >
                                    See my work
                                    <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                </Link>
                            </Magnetic>
                        </motion.div>
                    </motion.div>
                </motion.div>

                <div className='relative md:col-span-5'>
                    <motion.div
                        style={{ y: portraitY, scale: portraitScale }}
                        className='relative mx-auto w-full max-w-[300px] sm:max-w-[400px] md:max-w-[440px]'
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 40 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 1.1, ease: EASE_OUT, delay: 0.2 }}
                            className='relative aspect-[4/5] [perspective:1200px]'
                        >
                            <motion.div
                                aria-hidden='true'
                                style={{ rotate: blockRotate, scale: blockScale }}
                                className='absolute inset-x-0 bottom-0 top-[12%] rounded-[2rem] bg-secondary'
                            />

                            <motion.div
                                aria-hidden='true'
                                style={{ x: panelX }}
                                className='absolute left-[5%] right-[5%] top-[17%] h-[17%] rounded-2xl bg-white shadow-sm'
                            />

                            <motion.div
                                style={{ rotateX: tiltX, rotateY: tiltY }}
                                className='absolute inset-x-[10%] bottom-[5%] top-[3%] overflow-hidden rounded-[1.75rem] bg-cream shadow-[0_40px_80px_-30px_rgba(20,20,20,0.55)]'
                            >
                                {profile.portrait ? (
                                    <img
                                        src={profile.portrait}
                                        alt={profile.portrait_alt?.trim() || `Portrait of ${fullName}`}
                                        fetchPriority='high'
                                        decoding='async'
                                        className='size-full object-cover object-[50%_25%]'
                                    />
                                ) : (
                                    <div className='flex size-full items-center justify-center bg-primary font-heading text-8xl font-extrabold uppercase text-primary-foreground'>
                                        {initials}
                                    </div>
                                )}
                            </motion.div>

                            {STICKERS.map(sticker => (
                                <Sticker key={sticker.label} {...sticker} progress={progress} animated={animated} />
                            ))}

                            <RotatingBadge rotate={badgeRotate} />
                        </motion.div>
                    </motion.div>
                </div>
            </Container>
        </section>
    )
}
